import { lazy } from 'react';
import type { TIconComponent } from './registry';

// Not eager: `import.meta.glob` without `eager` keeps the keys (file names)
// available synchronously but turns each SVG into its own on-demand chunk.
// The registry spreads FEATURE_ICONS into COMMON_ICONS, which `<Icon>` — and
// so the main bundle — imports on every page; static imports here shipped all
// 28 compiled SVGs to pages that never show a plan feature list.
const featureIconLoaders = import.meta.glob<{ default: TIconComponent }>(
  '/src/assets/icons/features/*.svg',
  { query: '?react' }
);

/**
 * Icons for the plan feature list, keyed by the feature `id` the API returns
 * in `supported_features`.
 *
 * These differ from the rest of the registry in one important way: the key is
 * a RUNTIME value, not something a call site writes. `PlanFeaturesList` looks
 * the id up directly, so a feature the backend adds tomorrow resolves to
 * `default_icon` rather than rendering nothing — keep that fallback.
 *
 * Each id is the file's basename under assets/icons/features/, loaded lazily
 * (see above); `<Icon>` renders a lazy component inside its own Suspense.
 *
 * Every file was authored in a neutral #333333 ink, which `<Icon>` redirects
 * to `color`. The list passes `text.primary`, matching the `VideoIcon`
 * sibling that already read the theme, so the icons follow light/dark instead
 * of being a fixed near-black.
 */
export const FEATURE_ICONS: Record<string, TIconComponent> = Object.fromEntries(
  Object.entries(featureIconLoaders).map(([path, load]) => [
    path.replace(/^.*\/(.+)\.svg$/, '$1'),
    // LazyExoticComponent isn't a FunctionComponent to the type system, but
    // renders identically once resolved.
    lazy(load) as unknown as TIconComponent
  ])
);

export type TFeatureIconId = string;

/** Whether the API's feature id has an icon; `default_icon` covers the rest. */
export const hasFeatureIcon = (id: string): id is TFeatureIconId =>
  Object.prototype.hasOwnProperty.call(FEATURE_ICONS, id);
